/**
 * AI Contract Service - Frontend Implementation
 *
 * Handles AI metadata generation using the backend AI Response Contract
 * Replaces frontend AI processing with backend single source of truth
 *
 * Based on: /docs/ai-service/ai-response-contract-spec.md
 */

import api from '../http/axiosClient';
import { publicRequest } from '../http/request';
import { API_ENDPOINTS } from '../config/api-endpoints';
import {
  EXTRACT_ENDPOINT_BY_LAYER,
  EXTRACT_FILE_FIELD_BY_LAYER,
  ExtractLayerCode,
} from '../types/aiExtract.types';
import {
    AIResponseContract,
    LAYER_TYPE_MAP,
    LayerType
} from '../types/ai-response-contract.types';
import { AIContractValidator } from '../utils/ai-contract-validator';
import { conditionalLog } from '../utils/logger';
import {
    detectCulturalContext,
    normalizeHairColor,
    VOCABULARY_VERSION
} from '../utils/sharedVocabulary';
import { getBackendUrl } from '../utils/environment.config';
import {
  AGE_GROUP_VALUES,
  CULTURAL_CONTEXT_VALUES,
  GENRE_VALUES,
  GENDER_VALUES,
  INTENSITY_LEVEL_VALUES,
  MOOD_VALUES,
  PERFORMANCE_CONTEXT_VALUES,
} from '../contracts/openapi/assetContracts';

export interface AIGenerationRequest {
  layer: string; // Layer code (G, S, L, M, W)
  creatorDescription: string;
  fileUrl?: string; // Optional: URL to file (for backward compatibility)
  file?: File; // Optional: File object to send directly (preferred for multipart/form-data)
  additionalContext?: Record<string, any>;
  nnaAddress?: string; // Optional: For regeneration endpoints to enable auto-persistence
}

export interface AIGenerationResponse {
  success: boolean;
  data: {
    description: string;
    tags: string[];
    layerMetadata: Record<string, any>;
  };
  processingMethod: string;
  confidence: number;
  processingTimeMs: number;
  fallbackUsed: boolean;
  approach?: string;
  aiEnhanced?: boolean;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

/** Backend /ai/extract-metadata/song response: simplified payload for song assets (layer G). */
export interface SongMetadataDTO {
  songName?: string;
  artistName?: string;
  albumName?: string;
  mood?: string[];
  moods?: string[];
  tempo?: string | Record<string, unknown>;
  energy?: string | Record<string, unknown>;
  tags?: string[];
  description?: string;
  layerType?: string;
  genre?: string[];
  category?: string | string[];
  subcategory?: string | string[];
  bpm?: number;
  key?: string;
  originalArtistGender?: string;
  genderSuitability?: string;
  targetAudience?: string[];
  performanceContext?: string[];
  culturalContext?: string[];
  culturalInfluence?: string[];
  ageAppropriateness?: string[];
  instrumentalType?: string[];
  danceability?: string;
  releaseYear?: number;
}

export interface ExtractMetadataSongResponse {
  data: SongMetadataDTO;
  aiEnhanced?: boolean;
}

/** Backend /ai/extract-metadata/look response. */
export interface LookMetadataDTO {
  category?: string;
  subcategory?: string;
  type?: string;
  garmentType?: string;
  style?: string;
  formality?: string;
  occasion?: string;
  seasonality?: string;
  colorPalette?: string;
  tags?: string[];
  description?: string;
  name?: string;
  gender?: string;
  mood?: string[];
  primaryColors?: string[];
  targetAudience?: string[];
  performanceContext?: string[];
  culturalContext?: string[];
  materials?: string[];
  timeOfDay?: string;
  accessories?: string[];
}

export interface ExtractMetadataLookResponse {
  data: LookMetadataDTO;
  aiEnhanced?: boolean;
}

/** Backend /ai/extract-metadata/move response. */
export interface MoveMetadataDTO {
  category?: string;
  subcategory?: string;
  moveName?: string;
  danceStyle?: string;
  difficulty?: string;
  complexity?: string;
  energy?: string;
  mood?: string[];
  tempo?: string;
  bpm?: number;
  spaceRequired?: string;
  floorWork?: boolean;
  tags?: string[];
  description?: string;
  targetAudience?: string[];
  performanceContext?: string[];
}

export interface ExtractMetadataMoveResponse {
  data: MoveMetadataDTO;
  aiEnhanced?: boolean;
}

/** Backend /ai/extract-metadata/world response. */
export interface WorldMetadataDTO {
  category?: string;
  subcategory?: string;
  worldName?: string;
  locationType?: string;
  mood?: string[];
  spatialScale?: string;
  complexity?: string;
  lightingType?: string;
  colorPalette?: string;
  atmosphere?: string[] | string;
  timeContext?: string;
  weatherCondition?: string;
  season?: string;
  tags?: string[];
  description?: string;
  name?: string;
  targetAudience?: string[];
  performanceContext?: string[];
  culturalContext?: string[];
  lighting?: string;
  weather?: string;
  setting?: string;
}

export interface ExtractMetadataWorldResponse {
  data: WorldMetadataDTO;
  aiEnhanced?: boolean;
}

/** Backend /ai/extract-metadata/star response. */
export interface StarMetadataDTO {
  category?: string;
  subcategory?: string;
  starName?: string;
  name?: string;
  gender?: string;
  ageGroup?: string;
  ethnicity?: string;
  mood?: string[];
  skinTone?: string;
  eyeColor?: string;
  hairColor?: string;
  hairLength?: string;
  hairStyle?: string;
  makeupType?: string;
  colorPalette?: string;
  vibe?: string[];
  energy?: string;
  tags?: string[];
  description?: string;
  performanceContext?: string[];
  targetAudience?: string[];
  personalityTraits?: string[];
}

export interface ExtractMetadataStarResponse {
  data: StarMetadataDTO;
  aiEnhanced?: boolean;
}

class AIContractService {
  private readonly API_ENDPOINT = API_ENDPOINTS.AI.EXTRACT_METADATA;
  /**
   * Extract-metadata mapping matrix (backend key -> canonical field)
   * G: category/genre -> genre, moods/mood -> mood, culturalInfluence -> culturalContext
   * L: name -> outfitName, garmentType -> primaryGarment, colorPalette -> colorScheme
   * M: bpm -> bpm, tempo stays request-shape string, complexity may fallback to difficulty
   * W: name/worldName -> worldName, category -> locationType fallback, atmosphere does not imply mood
   * S: name/starName -> starName, vibe does not replace mood, targetAudience is optional only
   */

  private getWorldFallbackFileField(primaryField: string): string {
    if (primaryField === 'file') return 'videoFile';
    return 'file';
  }

  private async resolveFile(request: AIGenerationRequest): Promise<File> {
    if (request.file) return request.file;
    if (!request.fileUrl) {
      throw new Error(
        'File is required for AI metadata generation. The file was not provided in the request.'
      );
    }

    let blob: Blob;
    try {
      const response = await publicRequest<Blob>({
        url: request.fileUrl,
        responseType: 'blob',
      });
      blob = response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      throw new Error(
        status
          ? `Failed to fetch file for AI metadata generation: ${status}`
          : 'Failed to fetch file for AI metadata generation'
      );
    }

    const inferredName =
      request.fileUrl.split('/').pop() ||
      `asset.${request.layer === 'G' ? 'mp3' : request.layer === 'L' || request.layer === 'S' ? 'jpg' : 'mp4'}`;
    return new File([blob], inferredName, { type: blob.type || undefined });
  }

  /**
   * Generate AI metadata using backend contract
   * Now uses multipart/form-data format:
   * - imageFile (generic) or audioFile (song/layer G): The File object (if provided)
   * - requestData: JSON string with layer and creatorDescription
   */
  async generateMetadata(
    request: AIGenerationRequest
  ): Promise<AIGenerationResponse> {
    try {
      conditionalLog(
        `🤖 [AI Contract] Generating metadata for layer ${request.layer}`
      );
      // Build request URL and file field per backend contract.
      const layerCode = request.layer as ExtractLayerCode;
      const endpoint = EXTRACT_ENDPOINT_BY_LAYER[layerCode] ?? this.API_ENDPOINT;
      const url = `${getBackendUrl()}${endpoint}`;
      const fileFieldName = EXTRACT_FILE_FIELD_BY_LAYER[layerCode] ?? 'imageFile';

      // CRITICAL: Backend requires file to always be present (imageFile or audioFile)
      // Validate file exists in request
      const resolvedFile = await this.resolveFile(request);

      const requestData = {
        layer: request.layer,
        creatorDescription: request.creatorDescription,
        ...(request.additionalContext && request.additionalContext),
      };

      const formData = new FormData();

      // CRITICAL: Always append file to FormData under the correct field name
      // Backend validation requires this field to always be present
      formData.append(fileFieldName, resolvedFile);
      conditionalLog(
        `📎 [AI Contract] Added file to FormData (${fileFieldName}): ${resolvedFile.name} (${resolvedFile.size} bytes, type: ${resolvedFile.type})`
      );

      // Verify file was added
      if (!formData.has(fileFieldName)) {
        conditionalLog(
          `❌ [AI Contract] CRITICAL: ${fileFieldName} was not added to FormData!`
        );
        throw new Error('Failed to add file to FormData');
      }

      // Add requestData as JSON string
      formData.append('requestData', JSON.stringify(requestData));

      conditionalLog(`📦 [AI Contract] Request data:`, requestData);
      conditionalLog(`📦 [AI Contract] FormData entries:`, {
        [fileFieldName]: formData.has(fileFieldName),
        fileValue: resolvedFile
          ? `${resolvedFile.name} (${resolvedFile.size} bytes, ${resolvedFile.type})`
          : 'MISSING',
        hasRequestData: formData.has('requestData'),
        requestDataSize: formData.get('requestData')?.toString().length || 0,
      });

      let response;
      try {
        response = await api.post(url, formData);
      } catch (error: any) {
        // World endpoint contract has schema/prose mismatch in current OpenAPI ("file" vs "videoFile").
        // Retry once with alternate field name.
        if (request.layer === 'W' && error?.response?.status === 400) {
          const fallbackField = this.getWorldFallbackFileField(fileFieldName);
          const fallbackFormData = new FormData();
          fallbackFormData.append(fallbackField, resolvedFile);
          fallbackFormData.append('requestData', JSON.stringify(requestData));
          conditionalLog(
            `⚠️ [AI Contract] World upload retry with fallback field: ${fallbackField}`
          );
          response = await api.post(url, fallbackFormData);
        } else {
          throw error;
        }
      }
      const rawJson = response.data;

      // Song assets (layer G): new endpoint returns { data: SongMetadataDTO, aiEnhanced } — map to AIGenerationResponse and return
      if (request.layer === 'G') {
        const songResponse = rawJson as ExtractMetadataSongResponse;
        conditionalLog(
          '✅ [AI Contract] Received song extract response (extract-metadata/song)'
        );
        const layerMetadata = this.mapSongDTOToLayerMetadata(songResponse.data);
        const layerMetadataWithFallback =
          this.applyGenderDetectionFallback(layerMetadata);
        const finalResponse: AIGenerationResponse = {
          success: true,
          data: {
            description: songResponse.data?.description ?? '',
            tags: Array.isArray(songResponse.data?.tags)
              ? songResponse.data.tags
              : [],
            layerMetadata: layerMetadataWithFallback,
          },
          processingMethod: 'extract-metadata/song',
          confidence: 1,
          processingTimeMs: 0,
          fallbackUsed: false,
          aiEnhanced: songResponse.aiEnhanced,
        };
        conditionalLog(
          `🎯 [AI Contract] Returning song response with ${
            finalResponse.data.tags?.length ?? 0
          } tags`
        );
        return finalResponse;
      }

      // Look (L): dedicated endpoint returns { data: LookMetadataDTO, aiEnhanced }
      if (request.layer === 'L') {
        const lookResponse = rawJson as ExtractMetadataLookResponse;
        conditionalLog(
          '✅ [AI Contract] Received look extract response (extract-metadata/look)'
        );
        const layerMetadata = this.mapLookDTOToLayerMetadata(lookResponse.data);
        return {
          success: true,
          data: {
            description: lookResponse.data?.description ?? '',
            tags: Array.isArray(lookResponse.data?.tags)
              ? lookResponse.data.tags
              : [],
            layerMetadata,
          },
          processingMethod: 'extract-metadata/look',
          confidence: 1,
          processingTimeMs: 0,
          fallbackUsed: false,
          aiEnhanced: lookResponse.aiEnhanced,
        };
      }

      // Move (M): dedicated endpoint returns { data: MoveMetadataDTO, aiEnhanced }
      if (request.layer === 'M') {
        const moveResponse = rawJson as ExtractMetadataMoveResponse;
        conditionalLog(
          '✅ [AI Contract] Received move extract response (extract-metadata/move)'
        );
        const layerMetadata = this.mapMoveDTOToLayerMetadata(moveResponse.data);
        return {
          success: true,
          data: {
            description: moveResponse.data?.description ?? '',
            tags: Array.isArray(moveResponse.data?.tags)
              ? moveResponse.data.tags
              : [],
            layerMetadata,
          },
          processingMethod: 'extract-metadata/move',
          confidence: 1,
          processingTimeMs: 0,
          fallbackUsed: false,
          aiEnhanced: moveResponse.aiEnhanced,
        };
      }

      // World (W): dedicated endpoint returns { data: WorldMetadataDTO, aiEnhanced }
      if (request.layer === 'W') {
        const worldResponse = rawJson as ExtractMetadataWorldResponse;
        conditionalLog(
          '✅ [AI Contract] Received world extract response (extract-metadata/world)'
        );
        const layerMetadata = this.mapWorldDTOToLayerMetadata(
          worldResponse.data
        );
        return {
          success: true,
          data: {
            description: worldResponse.data?.description ?? '',
            tags: Array.isArray(worldResponse.data?.tags)
              ? worldResponse.data.tags
              : [],
            layerMetadata,
          },
          processingMethod: 'extract-metadata/world',
          confidence: 1,
          processingTimeMs: 0,
          fallbackUsed: false,
          aiEnhanced: worldResponse.aiEnhanced,
        };
      }

      // Star (S): dedicated endpoint returns { data: StarMetadataDTO, aiEnhanced }
      if (request.layer === 'S') {
        const starResponse = rawJson as ExtractMetadataStarResponse;
        conditionalLog(
          '✅ [AI Contract] Received star extract response (extract-metadata/star)'
        );
        const layerMetadata = this.mapStarDTOToLayerMetadata(starResponse.data);
        return {
          success: true,
          data: {
            description: starResponse.data?.description ?? '',
            tags: Array.isArray(starResponse.data?.tags)
              ? starResponse.data.tags
              : [],
            layerMetadata,
          },
          processingMethod: 'extract-metadata/star',
          confidence: 1,
          processingTimeMs: 0,
          fallbackUsed: false,
          aiEnhanced: starResponse.aiEnhanced,
        };
      }

      const responseData: AIResponseContract = rawJson as AIResponseContract;

      conditionalLog(
        `✅ [AI Contract] Received response in ${responseData.processingTimeMs}ms`
      );

      // Validate contract compliance (TEMPORARILY DISABLED FOR TESTING)
      // const validationResult = AIContractValidator.validate(responseData, request.layer);

      const data = responseData.data; // Backend provides data in responseData.data

      // CRITICAL DEBUGGING: Complete AI response structure analysis
      conditionalLog(
        '🔍 [AI CONTRACT DEBUG] ===== COMPLETE AI CONTRACT SERVICE RESPONSE ====='
      );
      conditionalLog(
        '🔍 [AI CONTRACT DEBUG] responseData:',
        JSON.stringify(responseData, null, 2)
      );
      conditionalLog(
        '🔍 [AI CONTRACT DEBUG] responseData.data:',
        JSON.stringify(data, null, 2)
      );

      // Check description at every possible path in the AI contract service
      conditionalLog(
        '🔍 [AI CONTRACT DESCRIPTION DEBUG] Description path verification:',
        {
          'responseData.description': (responseData as any)?.description,
          'responseData.data.description': data?.description,
          'description exists at nested data level': !!data?.description,
          'description length (nested data)': data?.description?.length || 0,
        }
      );

      conditionalLog(
        '🔍 [AI CONTRACT DEBUG] ===== END AI CONTRACT SERVICE ANALYSIS ====='
      );

      // ARCHITECTURAL SIMPLIFICATION: Use layerMetadata for all layers
      // This eliminates schema contract violations and provides clean abstraction
      let layerMetadata = data?.layerMetadata;

      // Backward compatibility: Transform layer-specific keys to layerMetadata
      if (!layerMetadata) {
        const legacyFields = {
          G: ['songMetadata'],
          S: ['starMetadata', 'starsMetadata'],
          L: ['looksMetadata'],
          M: ['movesMetadata'],
          W: ['worldsMetadata'],
          B: ['brandedMetadata'],
          C: ['compositeMetadata'],
          T: ['trainingMetadata'],
          R: ['rightsMetadata'],
          P: ['personalizeMetadata'],
        } as const;

        const possibleFields =
          legacyFields[request.layer as keyof typeof legacyFields] || [];

        for (const field of possibleFields) {
          if ((data as any)?.[field]) {
            layerMetadata = (data as any)[field];
            conditionalLog(
              `🔄 [Schema Migration] Converted ${field} → layerMetadata for layer ${request.layer}`
            );
            break;
          }
        }
      }

      // CRITICAL FIX: Handle double-nested layerMetadata structure from backend
      // Backend returns layerMetadata.layerMetadata for some layers
      if (layerMetadata && layerMetadata.layerMetadata) {
        conditionalLog(
          '🔧 [DOUBLE-NESTING FIX] Detected nested layerMetadata.layerMetadata structure, flattening...'
        );
        const nestedData = layerMetadata.layerMetadata;

        // Preserve the correctly nested data and override the defaults
        layerMetadata = {
          ...layerMetadata, // Keep any root-level fields
          ...nestedData, // Override with the actual nested data
        };

        conditionalLog('✅ [DOUBLE-NESTING FIX] Flattened structure applied');
      }

      // CRITICAL FIX: Handle field mapping mismatches for ALL layers
      if (layerMetadata) {
        // Songs Layer (G) - Fix field mappings
        if (request.layer === 'G') {
          // Fix vocalRange → vocalType field mapping mismatch
          if (layerMetadata.vocalRange && !layerMetadata.vocalType) {
            layerMetadata.vocalType = layerMetadata.vocalRange;
            conditionalLog(
              '🔧 [AI Contract] Fixed vocalRange → vocalType mapping:',
              layerMetadata.vocalRange
            );
          }
          // Fix songTitle → songName field mapping (current backend issue)
          if (layerMetadata.songTitle && !layerMetadata.songName) {
            layerMetadata.songName = layerMetadata.songTitle;
            conditionalLog(
              '🔧 [AI Contract] Fixed songTitle → songName mapping:',
              layerMetadata.songTitle
            );
          }
          // Fix album → albumName field mapping (current backend issue)
          if (layerMetadata.album && !layerMetadata.albumName) {
            layerMetadata.albumName = layerMetadata.album;
            conditionalLog(
              '🔧 [AI Contract] Fixed album → albumName mapping:',
              layerMetadata.album
            );
          }
          // Ensure required Songs fields exist
          if (!layerMetadata.layerType) {
            layerMetadata.layerType = 'songs';
            conditionalLog('🔧 [AI Contract] Added missing layerType field');
          }

          // INTELLIGENT DEFAULTS for Songs layer - Apply smart defaults for missing fields
          const smartDefaults = {
            songName: 'Untitled Song',
            artistName: 'Unknown Artist',
            albumName: 'Unknown Album',
            genre: ['Pop'],
            mood: ['Neutral'],
            tempo: 'Medium',
            energy: 'Medium',
            danceability: 'Medium',
            bpm: 120,
            duration: 180,
            key: 'C Major',
            timeSignature: '4/4',
            releaseYear: new Date().getFullYear(),
            explicit: false,
            language: ['English'],
            ageAppropriateness: ['all-ages'],
            originalArtistGender: 'unknown',
            genderSuitability: 'anyone',
            vocalType: 'medium',
            albumArt: '',
            instruments: ['vocals'],
            themes: ['general'],
            style: 'contemporary',
            production: 'studio',
            recordingQuality: 'high',
            popularity: 'underground',
            valence: 'neutral',
            acousticness: 'medium',
            instrumentalness: 'low',
            liveness: 'low',
            speechiness: 'low',
          };

          // Apply defaults for null/undefined values
          Object.keys(smartDefaults).forEach(key => {
            if (
              layerMetadata[key] === null ||
              layerMetadata[key] === undefined
            ) {
              layerMetadata[key] =
                smartDefaults[key as keyof typeof smartDefaults];
            }
          });

          // Special handling for language field to avoid backend validation errors
          if (layerMetadata.language) {
            const cleanLanguage = Array.isArray(layerMetadata.language)
              ? layerMetadata.language.filter(
                  (lang: any) =>
                    lang && typeof lang === 'string' && lang.trim() !== ''
                )
              : [];

            if (cleanLanguage.length === 0) {
              delete layerMetadata.language;
              conditionalLog(
                `🚨 [LANGUAGE WORKAROUND] Omitted empty language field to avoid backend validation error`
              );
            } else {
              layerMetadata.language = cleanLanguage;
            }
          }
        }

        // Stars Layer (S) - Fix field mappings and preserve inheritance
        else if (request.layer === 'S') {
          // Fix energyLevel → energy field mapping
          if (layerMetadata.energyLevel && !layerMetadata.energy) {
            layerMetadata.energy = layerMetadata.energyLevel;
            conditionalLog(
              '🔧 [AI Contract] Fixed energyLevel → energy mapping:',
              layerMetadata.energyLevel
            );
          }
          // Fix age → ageGroup field mapping
          if (layerMetadata.age && !layerMetadata.ageGroup) {
            layerMetadata.ageGroup = layerMetadata.age;
            conditionalLog(
              '🔧 [AI Contract] Fixed age → ageGroup mapping:',
              layerMetadata.age
            );
          }

          // 🛡️ ROBUST INHERITANCE PRESERVATION - Preserve inherited values when AI returns null
          // Check if this is a variant asset with inheritance context
          const hasInheritanceContext =
            request.additionalContext?.baseAsset ||
            request.additionalContext?.inheritedValues ||
            layerMetadata.baseStarId ||
            layerMetadata.baseAssetId ||
            layerMetadata[`base${request.layer}Id`] ||
            (request.creatorDescription &&
              request.creatorDescription.includes('variant'));

          if (hasInheritanceContext) {
            conditionalLog(
              '🛡️ [AI Contract] Variant asset detected - applying inheritance preservation'
            );

            // List of fields that should preserve inheritance when AI returns null
            const inheritableFields = [
              'eyeColor',
              'skinTone',
              'vibe',
              'makeupType',
              'colorPalette',
              'bodyType',
              'ethnicity',
              'realName',
              'careerStage',
            ];

            // Get inherited values from additional context if available
            const inheritedValues =
              request.additionalContext?.inheritedValues || {};

            inheritableFields.forEach(field => {
              if (
                layerMetadata[field] === null ||
                layerMetadata[field] === undefined
              ) {
                if (inheritedValues[field]) {
                  layerMetadata[field] = inheritedValues[field];
                  conditionalLog(
                    `🛡️ [AI Contract] Preserved inherited ${field}: "${inheritedValues[field]}" (AI returned null)`
                  );
                }
              }
            });

            // Special handling for nested physical traits
            if (layerMetadata.physicalTraits) {
              const physicalFields = [
                'eyeColor',
                'skinTone',
                'bodyType',
                'height',
              ];
              physicalFields.forEach(field => {
                if (
                  layerMetadata.physicalTraits[field] === null ||
                  layerMetadata.physicalTraits[field] === undefined
                ) {
                  if (inheritedValues[field]) {
                    layerMetadata.physicalTraits[field] =
                      inheritedValues[field];
                    conditionalLog(
                      `🛡️ [AI Contract] Preserved inherited physicalTraits.${field}: "${inheritedValues[field]}"`
                    );
                  }
                }
              });
            }

            conditionalLog(
              '🛡️ [AI Contract] Inheritance preservation completed'
            );
          }

          // 🎯 VOCABULARY ENHANCEMENT - Apply shared vocabulary contract (Frontend-Backend Coordination)
          conditionalLog(
            '🎯 [VOCABULARY] Applying shared vocabulary mappings and cultural context detection'
          );

          // Step 1: Hair Color Vocabulary Normalization
          if (layerMetadata.hairColor) {
            const originalHairColor = layerMetadata.hairColor;
            layerMetadata.hairColor = normalizeHairColor(
              layerMetadata.hairColor
            );
            if (originalHairColor !== layerMetadata.hairColor) {
              conditionalLog(
                `🎨 [VOCABULARY] Hair color normalized: "${originalHairColor}" → "${layerMetadata.hairColor}"`
              );
            }
          }

          // Handle nested hair color in physicalTraits
          if (layerMetadata.physicalTraits?.hairColor) {
            const originalHairColor = layerMetadata.physicalTraits.hairColor;
            layerMetadata.physicalTraits.hairColor = normalizeHairColor(
              layerMetadata.physicalTraits.hairColor
            );
            if (originalHairColor !== layerMetadata.physicalTraits.hairColor) {
              conditionalLog(
                `🎨 [VOCABULARY] PhysicalTraits hair color normalized: "${originalHairColor}" → "${layerMetadata.physicalTraits.hairColor}"`
              );
            }
          }

          // Step 2: Cultural Context Detection for Ethnicity & Musical Style
          if (request.creatorDescription) {
            const culturalContext = detectCulturalContext(
              request.creatorDescription
            );

            // Apply ethnicity if detected and current is empty
            if (
              culturalContext.ethnicity &&
              (!layerMetadata.ethnicity || layerMetadata.ethnicity === '')
            ) {
              layerMetadata.ethnicity = culturalContext.ethnicity;
              conditionalLog(
                `🌍 [SHARED VOCABULARY v${VOCABULARY_VERSION}] Ethnicity detected: "${culturalContext.ethnicity}"`
              );
            }

            // Apply musical styles if detected and current is empty
            if (
              culturalContext.musicalStyles &&
              (!layerMetadata.musicalStyle ||
                layerMetadata.musicalStyle.length === 0)
            ) {
              layerMetadata.musicalStyle = culturalContext.musicalStyles;
              conditionalLog(
                `🎵 [SHARED VOCABULARY v${VOCABULARY_VERSION}] Musical styles detected: [${culturalContext.musicalStyles.join(
                  ', '
                )}]`
              );
            }
          }

          // INTELLIGENT DEFAULTS for Stars layer - Apply smart defaults for missing fields
          const starsSmartDefaults = {
            starName: 'Unknown Star',
            realName: '',
            gender: 'unknown',
            ageGroup: this.getTaxonomyAwareAgeGroup(
              request.layer,
              request.additionalContext?.subcategory
            ),
            ethnicity: '',
            physicalTraits: {
              bodyType: '',
              height: '',
              hairColor: '',
              hairLength: 'medium',
              hairStyle: 'natural',
              eyeColor: '',
              skinTone: '',
            },
            archetype: 'performer',
            musicalStyle: [],
            careerStage: 'emerging',
            energy: 'medium',
            personalityTraits: [],
            vibe: 'neutral',
            makeupType: 'natural',
            colorPalette: 'neutral',
            hairTexture: 'straight',
            hairTreatment: 'natural',
            hairAccessories: '',
            eyeMakeup: 'minimal',
            lipMakeup: 'natural',
            faceMakeup: 'light',
            makeupEffects: 'none',
            assetType: 'base',
            baseStarId: '',
            variantName: '',
            variantType: '',
            layerType: 'stars',
          };

          // Apply defaults for null/undefined values
          // CRITICAL FIX: Don't override AI-detected values in root layerMetadata
          Object.keys(starsSmartDefaults).forEach(key => {
            if (key === 'physicalTraits') {
              // Handle nested physicalTraits object
              if (!layerMetadata.physicalTraits) {
                layerMetadata.physicalTraits = {};
              }
              Object.keys(starsSmartDefaults.physicalTraits).forEach(
                traitKey => {
                  // CRITICAL FIX: Check if AI already detected this value in nested layerMetadata
                  const nestedAI = layerMetadata.layerMetadata || {};
                  const aiDetectedValue =
                    layerMetadata[traitKey] || nestedAI[traitKey];
                  const hasAiValue =
                    aiDetectedValue &&
                    aiDetectedValue !== '' &&
                    aiDetectedValue !== null &&
                    aiDetectedValue !== undefined;

                  if (
                    layerMetadata.physicalTraits[traitKey] === null ||
                    layerMetadata.physicalTraits[traitKey] === undefined
                  ) {
                    if (hasAiValue) {
                      // Use AI-detected value instead of default
                      layerMetadata.physicalTraits[traitKey] = aiDetectedValue;
                    } else {
                      // Use smart default
                      layerMetadata.physicalTraits[traitKey] =
                        starsSmartDefaults.physicalTraits[
                          traitKey as keyof typeof starsSmartDefaults.physicalTraits
                        ];
                    }
                  }
                }
              );
            } else {
              // CRITICAL FIX: Only apply defaults for truly missing fields, not when AI has detected values
              if (
                layerMetadata[key] === null ||
                layerMetadata[key] === undefined
              ) {
                // Special handling for starName - only apply default if no AI detection at all
                if (key === 'starName') {
                  // Check if AI actually detected star name data in nested layerMetadata
                  const nestedAI = layerMetadata.layerMetadata || {};
                  const hasAIStarName =
                    nestedAI.starName && nestedAI.starName !== 'Unknown Star';
                  const hasAnyStarData =
                    layerMetadata.realName ||
                    layerMetadata.artistName ||
                    layerMetadata.performerName ||
                    layerMetadata.characterName ||
                    hasAIStarName;
                  if (!hasAnyStarData) {
                    layerMetadata[key] =
                      starsSmartDefaults[
                        key as keyof typeof starsSmartDefaults
                      ];
                  } else {
                    // If AI detected starName in nested layerMetadata, use that
                    if (hasAIStarName) {
                      layerMetadata[key] = nestedAI.starName;
                    }
                  }
                } else if (key === 'gender') {
                  // Special handling for gender - check nested AI data
                  const nestedAI = layerMetadata.layerMetadata || {};
                  const hasAIGender =
                    nestedAI.gender && nestedAI.gender !== 'unknown';
                  if (hasAIGender) {
                    layerMetadata[key] = nestedAI.gender;
                  } else {
                    layerMetadata[key] =
                      starsSmartDefaults[
                        key as keyof typeof starsSmartDefaults
                      ];
                  }
                } else if (key === 'musicalStyle') {
                  // Special handling for musicalStyle - check nested AI data
                  const nestedAI = layerMetadata.layerMetadata || {};
                  const hasAIMusicalStyle =
                    nestedAI.musicalStyle &&
                    Array.isArray(nestedAI.musicalStyle) &&
                    nestedAI.musicalStyle.length > 0;
                  if (hasAIMusicalStyle) {
                    layerMetadata[key] = nestedAI.musicalStyle;
                  } else {
                    layerMetadata[key] =
                      starsSmartDefaults[
                        key as keyof typeof starsSmartDefaults
                      ];
                  }
                } else {
                  layerMetadata[key] =
                    starsSmartDefaults[key as keyof typeof starsSmartDefaults];
                }
              }
            }
          });
        }

        // Looks Layer (L) - Handle legacy field mappings
        else if (request.layer === 'L') {
          // Fix brandName → brandNames field mapping
          if (layerMetadata.brandName && !layerMetadata.brandNames) {
            layerMetadata.brandNames = layerMetadata.brandName;
            conditionalLog(
              '🔧 [AI Contract] Fixed brandName → brandNames mapping:',
              layerMetadata.brandName
            );
          }
          // Ensure arrays are properly formatted
          ['primaryColors', 'patterns', 'materials', 'accessories'].forEach(
            field => {
              if (
                layerMetadata[field] &&
                !Array.isArray(layerMetadata[field])
              ) {
                layerMetadata[field] = [layerMetadata[field]];
              }
            }
          );

          // INTELLIGENT DEFAULTS for Looks layer - Apply smart defaults for missing fields
          const looksSmartDefaults = {
            lookName: 'Untitled Look',
            style: 'casual',
            category: 'clothing',
            subcategory: '',
            brandNames: [],
            designer: '',
            primaryColors: ['neutral'],
            secondaryColors: [],
            patterns: [],
            materials: ['cotton'],
            accessories: [],
            occasion: 'daily',
            season: 'all-season',
            targetGender: 'unisex',
            targetAge: 'adult',
            priceRange: 'moderate',
            formalityLevel: 'casual',
            versatility: 'medium',
            trendiness: 'timeless',
            comfort: 'comfortable',
            durability: 'good',
            careInstructions: 'machine-washable',
            sustainability: 'standard',
            culturalSignificance: '',
            layerType: 'looks',
            assetType: 'base',
            baseLookId: '',
            variantName: '',
            variantType: '',
          };

          // Apply defaults for null/undefined values
          Object.keys(looksSmartDefaults).forEach(key => {
            if (
              layerMetadata[key] === null ||
              layerMetadata[key] === undefined
            ) {
              layerMetadata[key] =
                looksSmartDefaults[key as keyof typeof looksSmartDefaults];
            }
          });
        }

        // Moves Layer (M) - Handle enhanced fields
        else if (request.layer === 'M') {
          // Fix energyLevel → energy field mapping
          if (layerMetadata.energyLevel && !layerMetadata.energy) {
            layerMetadata.energy = layerMetadata.energyLevel;
            conditionalLog(
              '🔧 [AI Contract] Fixed energyLevel → energy mapping for Moves'
            );
          }
          // Ensure arrays are properly formatted
          [
            'bodyParts',
            'signature_moves',
            'techniques_required',
            'props_needed',
          ].forEach(field => {
            if (layerMetadata[field] && !Array.isArray(layerMetadata[field])) {
              layerMetadata[field] = [layerMetadata[field]];
            }
          });

          // INTELLIGENT DEFAULTS for Moves layer - Apply smart defaults for missing fields
          const movesSmartDefaults = {
            moveName: 'Untitled Move',
            choreographer: '',
            style: 'contemporary',
            difficulty: 'intermediate',
            energy: 'medium',
            tempo: 'medium',
            duration: 30, // seconds
            bodyParts: ['full-body'],
            signature_moves: [],
            techniques_required: ['basic'],
            props_needed: [],
            musicGenre: 'general',
            formation: 'solo',
            stageRequirement: 'small',
            targetAudience: 'general',
            culturalOrigin: '',
            ageAppropriate: 'all-ages',
            practiceTime: 'moderate',
            physicalDemand: 'medium',
            coordination: 'medium',
            flexibility: 'basic',
            layerType: 'moves',
            assetType: 'base',
            baseMoveId: '',
            variantName: '',
            variantType: '',
          };

          // Apply defaults for null/undefined values
          Object.keys(movesSmartDefaults).forEach(key => {
            if (
              layerMetadata[key] === null ||
              layerMetadata[key] === undefined
            ) {
              layerMetadata[key] =
                movesSmartDefaults[key as keyof typeof movesSmartDefaults];
            }
          });
        }

        // Worlds Layer (W) - Handle enhanced fields
        else if (request.layer === 'W') {
          // Ensure arrays are properly formatted
          ['dominant_colors'].forEach(field => {
            if (layerMetadata[field] && !Array.isArray(layerMetadata[field])) {
              layerMetadata[field] = [layerMetadata[field]];
            }
          });

          // INTELLIGENT DEFAULTS for Worlds layer - Apply smart defaults for missing fields
          const worldsSmartDefaults = {
            worldName: 'Untitled World',
            environment: 'indoor',
            setting: 'studio',
            atmosphere: 'neutral',
            mood: 'calm',
            lighting: 'natural',
            timeOfDay: 'day',
            season: 'spring',
            weather: 'clear',
            architecture: 'modern',
            style: 'contemporary',
            scale: 'medium',
            capacity: 'small-group',
            acoustics: 'good',
            visibility: 'clear',
            accessibility: 'accessible',
            dominant_colors: ['neutral'],
            textures: [],
            materials: [],
            props: [],
            technology: 'basic',
            interactivity: 'static',
            mobility: 'fixed',
            versatility: 'moderate',
            maintenance: 'low',
            // REMOVED: culturalContext - use cultural_context (string) instead
            layerType: 'worlds',
            assetType: 'base',
            baseWorldId: '',
            variantName: '',
            variantType: '',
          };

          // Apply defaults for null/undefined values
          Object.keys(worldsSmartDefaults).forEach(key => {
            if (
              layerMetadata[key] === null ||
              layerMetadata[key] === undefined
            ) {
              layerMetadata[key] =
                worldsSmartDefaults[key as keyof typeof worldsSmartDefaults];
            }
          });

          // CRITICAL: Delete culturalContext if it exists (we use cultural_context string instead)
          if (
            layerMetadata.culturalContext &&
            typeof layerMetadata.culturalContext === 'object' &&
            !Array.isArray(layerMetadata.culturalContext)
          ) {
            delete layerMetadata.culturalContext;
          }
        }

        // Composite Layer (C) - Handle component structure
        else if (request.layer === 'C') {
          // Ensure components object structure exists
          if (
            !layerMetadata.components &&
            (layerMetadata.song ||
              layerMetadata.star ||
              layerMetadata.look ||
              layerMetadata.moves ||
              layerMetadata.world)
          ) {
            layerMetadata.components = {
              song: layerMetadata.song || '',
              star: layerMetadata.star || '',
              look: layerMetadata.look || '',
              moves: layerMetadata.moves || '',
              world: layerMetadata.world || '',
            };
            conditionalLog(
              '🔧 [AI Contract] Fixed components object structure'
            );
          }
        }
      }

      conditionalLog(
        `🔧 [AI Contract] Processed layerMetadata for layer ${request.layer}`
      );

      // Clean generic AI prefixes from description
      let cleanDescription = data?.description || '';
      if (
        cleanDescription.startsWith('AI-generated metadata for stars layer:')
      ) {
        cleanDescription = cleanDescription
          .replace('AI-generated metadata for stars layer:', '')
          .trim();
        conditionalLog('🧹 [AI Contract] Cleaned generic description prefix');
      }

      // Add layerType to metadata for proper identification
      if (layerMetadata && !layerMetadata.layerType) {
        const layerTypeMap = {
          G: 'songs',
          S: 'stars',
          L: 'looks',
          M: 'moves',
          W: 'worlds',
          C: 'composite',
        } as const;
        layerMetadata.layerType =
          layerTypeMap[request.layer as keyof typeof layerTypeMap] ||
          request.layer;
        conditionalLog(
          `🏷️ [AI Contract] Added layerType: ${layerMetadata.layerType}`
        );
      }

      // 🏷️ TAGS: Use pre-filtered tags from backend AI response
      // Backend AI Enhancement already provides comprehensive, filtered tags
      const filteredTags = data?.tags || [];
      conditionalLog(
        `🏷️ [TAGS] Using ${filteredTags.length} pre-filtered tags from backend AI response`
      );

      // 🚨 CRITICAL FIX: Frontend Gender Detection Fallback
      // Backend AI is incorrectly detecting some artists - apply frontend corrections
      if (layerMetadata && request.layer === 'G') {
        layerMetadata = this.applyGenderDetectionFallback(layerMetadata);
      }

      // ARCHITECTURAL SIMPLIFICATION: Return only layerMetadata (no layer-specific duplicates)
      const finalResponse = {
        success: responseData.success,
        data: {
          description: cleanDescription,
          tags: filteredTags, // Pre-filtered tags from backend AI response
          layerMetadata: layerMetadata || {},
        },
        processingMethod: responseData.processingMethod,
        confidence: responseData.confidence,
        processingTimeMs: responseData.processingTimeMs,
        fallbackUsed: responseData.fallbackUsed,
        approach: responseData.approach,
        error: responseData.error,
      };

      // FINAL RESPONSE DEBUG
      conditionalLog(
        '🔍 [AI CONTRACT FINAL RESPONSE DEBUG] ===== FINAL RESPONSE CONSTRUCTION ====='
      );
      conditionalLog(
        '🔍 [AI CONTRACT FINAL RESPONSE DEBUG] cleanDescription:',
        cleanDescription
      );
      conditionalLog(
        '🔍 [AI CONTRACT FINAL RESPONSE DEBUG] filteredTags count:',
        filteredTags?.length || 0
      );
      conditionalLog(
        '🔍 [AI CONTRACT FINAL RESPONSE DEBUG] ===== END FINAL RESPONSE DEBUG ====='
      );

      conditionalLog(
        `🎯 [AI Contract] Returning response for layer ${request.layer} with ${
          finalResponse.data.tags?.length || 0
        } pre-filtered tags from backend`
      );

      // Return the response with proper metadata structure
      return finalResponse;
    } catch (error: any) {
      console.error('❌ [AI Contract] API call failed:', error);

      // Extract error message
      let errorMessage = 'Network error connecting to AI service';
      let errorCode = 'NETWORK_ERROR';
      let errorDetails = error;

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        data: {
          description: '',
          tags: [],
          layerMetadata: {},
        },
        processingMethod: 'error',
        confidence: 0,
        processingTimeMs: 0,
        fallbackUsed: false,
        error: {
          message: errorMessage,
          code: errorCode,
          details: errorDetails,
        },
      };
    }
  }

  /**
   * Generate metadata for Songs layer with enhanced context
   */
  async generateSongsMetadata(
    creatorDescription: string,
    fileUrl?: string
  ): Promise<AIGenerationResponse> {
    // For Songs layer, provide a placeholder URL if no actual file URL is provided
    const effectiveFileUrl =
      fileUrl || 'https://placeholder.example.com/song.mp3';

    return this.generateMetadata({
      layer: 'G',
      creatorDescription,
      fileUrl: effectiveFileUrl,
      additionalContext: {
        includeAlbumArt: true,
        includeBPM: true,
      },
    });
  }

  /**
   * Map SongMetadataDTO (from /ai/extract-metadata/song) to layerMetadata shape expected by the form.
   */
  private mapSongDTOToLayerMetadata(dto: SongMetadataDTO): Record<string, any> {
    const genre = this.coerceEnumArray(
      dto.genre ?? dto.category,
      GENRE_VALUES
    );
    const mood = this.coerceEnumArray(dto.mood ?? dto.moods, MOOD_VALUES);
    const energy = this.normalizeIntensity(dto.energy);
    const tempo = this.normalizeTempoEnum(dto.tempo, dto.bpm);
    const targetAudience = this.coerceEnumArray(dto.targetAudience, AGE_GROUP_VALUES);
    const performanceContext = this.coerceEnumArray(dto.performanceContext, PERFORMANCE_CONTEXT_VALUES);
    const culturalContext = this.coerceEnumArray(
      dto.culturalContext ?? dto.culturalInfluence,
      CULTURAL_CONTEXT_VALUES
    );
    return {
      songName: dto.songName ?? '',
      artistName: dto.artistName ?? '',
      albumName: dto.albumName ?? '',
      mood,
      genre,
      tags: Array.isArray(dto.tags) ? dto.tags : [],
      description: dto.description ?? '',
      layerType: dto.layerType ?? 'songs',
      bpm: dto.bpm,
      key: dto.key,
      originalArtistGender: dto.originalArtistGender,
      genderSuitability: dto.genderSuitability,
      energy,
      tempo,
      targetAudience,
      performanceContext,
      culturalContext,
      ageAppropriateness: Array.isArray(dto.ageAppropriateness) ? dto.ageAppropriateness : [],
      instrumentalType: Array.isArray(dto.instrumentalType) ? dto.instrumentalType : [],
      danceability: dto.danceability,
      releaseYear: dto.releaseYear,
      musicalStyle: genre,
    };
  }

  /** Map LookMetadataDTO to layerMetadata shape expected by LooksLayerStrategy / form. */
  private mapLookDTOToLayerMetadata(dto: LookMetadataDTO): Record<string, any> {
    return {
      layerType: 'looks',
      outfitName: dto.name ?? '',
      styleCategory: dto.category ?? '',
      style: dto.style ?? dto.subcategory ?? '',
      gender: this.coerceEnumValue(dto.gender, GENDER_VALUES),
      occasion: dto.occasion ?? '',
      colorScheme: dto.colorPalette ?? '',
      primaryGarment: dto.garmentType ?? '',
      mood: this.coerceEnumArray(dto.mood, MOOD_VALUES),
      primaryColors: Array.isArray(dto.primaryColors) ? dto.primaryColors : [],
      formality: dto.formality ?? '',
      description: dto.description ?? '',
      targetAudience: this.coerceEnumArray(dto.targetAudience, AGE_GROUP_VALUES),
      performanceContext: this.coerceEnumArray(dto.performanceContext, PERFORMANCE_CONTEXT_VALUES),
      culturalContext: this.coerceEnumArray(dto.culturalContext, CULTURAL_CONTEXT_VALUES),
      materials: Array.isArray(dto.materials) ? dto.materials : [],
      seasonality: dto.seasonality,
      colorPalette: dto.colorPalette,
      timeOfDay: dto.timeOfDay,
      accessories: Array.isArray(dto.accessories) ? dto.accessories : [],
      tags: Array.isArray(dto.tags) ? dto.tags : [],
    };
  }

  /** Map MoveMetadataDTO to layerMetadata shape expected by MovesLayerStrategy / form. */
  private mapMoveDTOToLayerMetadata(dto: MoveMetadataDTO): Record<string, any> {
    const difficulty = dto.difficulty ?? dto.complexity ?? '';
    return {
      layerType: 'moves',
      moveName: dto.moveName ?? '',
      danceStyle: dto.danceStyle ?? dto.category ?? '',
      difficulty,
      mood: this.coerceEnumArray(dto.mood, MOOD_VALUES),
      description: dto.description ?? '',
      complexity: dto.complexity ?? undefined,
      intensity: dto.energy ?? undefined,
      energy: this.normalizeIntensity(dto.energy),
      tempo: this.normalizeTempoEnum(dto.tempo, dto.bpm),
      bpm: dto.bpm,
      spaceRequired: dto.spaceRequired ?? undefined,
      floorWork: dto.floorWork,
      targetAudience: this.coerceEnumArray(dto.targetAudience, AGE_GROUP_VALUES),
      performanceContext: this.coerceEnumArray(dto.performanceContext, PERFORMANCE_CONTEXT_VALUES),
      tags: Array.isArray(dto.tags) ? dto.tags : [],
    };
  }

  /** Map WorldMetadataDTO to layerMetadata shape expected by WorldsLayerStrategy / form. */
  private mapWorldDTOToLayerMetadata(dto: WorldMetadataDTO): Record<string, any> {
    return {
      layerType: 'worlds',
      worldName: dto.worldName ?? dto.name ?? '',
      locationType: dto.locationType ?? dto.category ?? '',
      mood: this.coerceEnumArray(dto.mood, MOOD_VALUES),
      description: dto.description ?? '',
      scale: dto.spatialScale ?? undefined,
      complexity: dto.complexity ?? undefined,
      lighting: dto.lighting ?? dto.lightingType ?? undefined,
      colorPalette: dto.colorPalette,
      atmosphere:
        typeof dto.atmosphere === 'string'
          ? dto.atmosphere
          : Array.isArray(dto.atmosphere)
          ? dto.atmosphere.join(', ')
          : undefined,
      timeOfDay: dto.timeContext ?? undefined,
      weather: dto.weather ?? dto.weatherCondition ?? undefined,
      season: dto.season,
      setting: dto.setting,
      targetAudience: this.coerceEnumArray(dto.targetAudience, AGE_GROUP_VALUES),
      performanceContext: this.coerceEnumArray(dto.performanceContext, PERFORMANCE_CONTEXT_VALUES),
      culturalContext: this.coerceEnumArray(dto.culturalContext, CULTURAL_CONTEXT_VALUES),
      tags: Array.isArray(dto.tags) ? dto.tags : [],
    };
  }

  /** Map StarMetadataDTO to layerMetadata shape expected by StarsLayerStrategy / form. */
  private mapStarDTOToLayerMetadata(dto: StarMetadataDTO): Record<string, any> {
    return {
      layerType: 'stars',
      starName: dto.starName ?? dto.name ?? '',
      description: dto.description ?? '',
      gender: this.coerceEnumValue(dto.gender, GENDER_VALUES),
      ageGroup: dto.ageGroup ?? undefined,
      ethnicity: dto.ethnicity ?? '',
      mood: this.coerceEnumArray(dto.mood, MOOD_VALUES),
      skinTone: dto.skinTone ?? undefined,
      eyeColor: dto.eyeColor ?? undefined,
      hairColor: dto.hairColor ?? undefined,
      hairLength: dto.hairLength ?? undefined,
      hairStyle: dto.hairStyle ?? undefined,
      makeupType: dto.makeupType ?? undefined,
      colorPalette: dto.colorPalette ?? undefined,
      vibe: Array.isArray(dto.vibe) && dto.vibe.length ? dto.vibe.join(', ') : undefined,
      energy: this.normalizeIntensity(dto.energy),
      performanceContext: this.coerceEnumArray(dto.performanceContext, PERFORMANCE_CONTEXT_VALUES),
      targetAudience: this.coerceEnumArray(dto.targetAudience, AGE_GROUP_VALUES),
      personalityTraits: Array.isArray(dto.personalityTraits) ? dto.personalityTraits : [],
      tags: Array.isArray(dto.tags) ? dto.tags : [],
    };
  }

  private normalizeIntensity(
    energy: string | Record<string, unknown> | undefined
  ): string | undefined {
    if (energy == null) return undefined;
    const s =
      typeof energy === 'string'
        ? energy
        : (energy as { name?: string })?.name ?? (energy as { value?: string })?.value ?? '';
    return this.coerceEnumValue(s, INTENSITY_LEVEL_VALUES);
  }

  private normalizeTempoEnum(
    tempo: string | Record<string, unknown> | undefined,
    bpm: number | undefined
  ): string | undefined {
    if (tempo == null) return undefined;
    const s =
      typeof tempo === 'string'
        ? tempo
        : (tempo as { name?: string })?.name ?? (tempo as { value?: string })?.value ?? '';
    const lower = String(s).toLowerCase();
    if (lower === 'slow') return 'SLOW';
    if (lower === 'medium' || lower === 'mid' || (bpm != null && bpm >= 80 && bpm <= 120)) return 'MEDIUM';
    if (lower === 'fast' || (bpm != null && bpm > 120)) return 'FAST';
    if (bpm != null && bpm < 80) return 'SLOW';
    return typeof s === 'string' && s.trim() !== '' ? s.toUpperCase() : undefined;
  }

  private coerceEnumArray<T extends string>(
    value: unknown,
    allowed: readonly T[]
  ): T[] {
    const list = Array.isArray(value) ? value : value == null ? [] : [value];
    const allowedSet = new Set<string>(allowed as readonly string[]);
    const result: T[] = [];
    for (const item of list) {
      const normalized = String(item).trim().toUpperCase().replace(/[\s-]+/g, '_');
      if (allowedSet.has(normalized) && !result.includes(normalized as T)) {
        result.push(normalized as T);
      }
    }
    return result;
  }

  private coerceEnumValue<T extends string>(
    value: unknown,
    allowed: readonly T[]
  ): T | undefined {
    return this.coerceEnumArray(value, allowed)[0];
  }

  /**
   * 🚨 CRITICAL FIX: Frontend Gender Detection Fallback
   * Corrects backend AI gender detection errors for known artists
   */
  private applyGenderDetectionFallback(layerMetadata: any): any {
    if (!layerMetadata?.artistName) {
      return layerMetadata;
    }

    // Known gender corrections for artists where backend AI is incorrect
    const genderCorrections: { [key: string]: string } = {
      'miley cyrus': 'Non-binary',
      'demi lovato': 'Non-binary',
      'sam smith': 'Non-binary',
      'janelle monáe': 'Non-binary',
      'halsey': 'Non-binary',
      'kehlani': 'Non-binary',
      // Add more as needed
    };

    const artistNameLower = layerMetadata.artistName.toLowerCase();
    const correctedGender = genderCorrections[artistNameLower];

    if (correctedGender && layerMetadata.originalArtistGender !== correctedGender) {
      conditionalLog(`🚨 [GENDER CORRECTION] "${layerMetadata.artistName}": ${layerMetadata.originalArtistGender} → ${correctedGender}`);

      return {
        ...layerMetadata,
        originalArtistGender: correctedGender,
        // Also update other gender-related fields for consistency
        genderSuitability: correctedGender === 'Non-binary' ? 'All' : layerMetadata.genderSuitability,
      };
    }

    return layerMetadata;
  }

  /**
   * Generate metadata for Stars layer
   */
  async generateStarsMetadata(
    creatorDescription: string,
    imageUrl?: string
  ): Promise<AIGenerationResponse> {
    // For Stars layer, provide a placeholder URL if no actual image URL is provided
    const effectiveFileUrl =
      imageUrl || 'https://placeholder.example.com/star.jpg';

    return this.generateMetadata({
      layer: 'S',
      creatorDescription,
      fileUrl: effectiveFileUrl,
    });
  }

  /**
   * Generate metadata for Looks layer
   */
  async generateLooksMetadata(
    creatorDescription: string,
    imageUrl?: string
  ): Promise<AIGenerationResponse> {
    // For Looks layer, provide a placeholder URL if no actual image URL is provided
    const effectiveFileUrl =
      imageUrl || 'https://placeholder.example.com/look.jpg';

    return this.generateMetadata({
      layer: 'L',
      creatorDescription,
      fileUrl: effectiveFileUrl,
    });
  }

  /**
   * Generate metadata for Moves layer
   */
  async generateMovesMetadata(
    creatorDescription: string,
    videoUrl?: string
  ): Promise<AIGenerationResponse> {
    // For Moves layer, provide a placeholder URL if no actual video URL is provided
    const effectiveFileUrl =
      videoUrl || 'https://placeholder.example.com/moves.mp4';

    return this.generateMetadata({
      layer: 'M',
      creatorDescription,
      fileUrl: effectiveFileUrl,
      additionalContext: {
        extractFrames: true,
        frameCount: 5,
      },
    });
  }

  /**
   * Generate metadata for Worlds layer
   */
  async generateWorldsMetadata(
    creatorDescription: string,
    fileUrl?: string
  ): Promise<AIGenerationResponse> {
    // For Worlds layer, provide a placeholder URL if no actual file URL is provided
    const effectiveFileUrl =
      fileUrl || 'https://placeholder.example.com/world.mp4';

    return this.generateMetadata({
      layer: 'W',
      creatorDescription,
      fileUrl: effectiveFileUrl,
      additionalContext: {
        extractFrames: true,
        frameCount: 5,
      },
    });
  }

  /**
   * Regenerate only description for existing asset using optimized backend endpoint
   */
  async regenerateDescription(
    request: AIGenerationRequest
  ): Promise<AIGenerationResponse> {
    throw new Error(
      `Description regeneration is deprecated and unsupported by current API for layer ${request.layer}.`
    );
  }

  /**
   * Regenerate only tags for existing asset using optimized backend endpoint
   */
  async regenerateTags(
    request: AIGenerationRequest
  ): Promise<AIGenerationResponse> {
    throw new Error(
      `Tag regeneration is deprecated and unsupported by current API for layer ${request.layer}.`
    );
  }

  /**
   * Get taxonomy-aware age group defaults for Stars layer based on subcategory
   */
  private getTaxonomyAwareAgeGroup(
    layer: string,
    subcategory?: string
  ): string {
    if (layer !== 'S' || !subcategory) {
      return 'adult'; // Default fallback
    }

    // Taxonomy-aware age group mapping based on NNA taxonomy
    const ageGroupMapping = {
      // Children subcategories
      CHI: 'child',
      GIR: 'child',
      BOY: 'child',

      // Teen/Young subcategories
      YOU: 'young_adult', // S.GRL.YOU should map to young_adult, not child
      TEE: 'teen',
      TEN: 'teen',

      // Adult subcategories
      ADU: 'adult',
      WOM: 'adult',
      MAN: 'adult',
      MAL: 'adult',
      FEM: 'adult',

      // Mature subcategories
      MAT: 'mature',
      SEN: 'senior',
      ELD: 'senior',
    };

    const ageGroup =
      ageGroupMapping[subcategory as keyof typeof ageGroupMapping];

    if (ageGroup) {
      conditionalLog(
        `⭐ [TAXONOMY-AWARE AGE] ${layer}.*.${subcategory} → ${ageGroup}`
      );
      return ageGroup;
    }

    conditionalLog(
      `⭐ [TAXONOMY-AWARE AGE] ${layer}.*.${subcategory} → adult (default)`
    );
    return 'adult'; // Default fallback
  }

  /**
   * Get layer type from layer code
   */
  private getLayerType(layerCode: string): LayerType {
    return LAYER_TYPE_MAP[layerCode] || (layerCode.toLowerCase() as LayerType);
  }

  /**
   * Debug helper to log contract validation results
   */
  debugValidation(response: AIResponseContract, layer: string): void {
    AIContractValidator.logValidation(response, layer, `Layer ${layer}`);
  }
}

// Export singleton instance
export default new AIContractService();
